import { useCallback } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import PropTypes from 'prop-types';

import ConfirmModal from '#client/components/ConfirmModal';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useToastContext from '#client/hooks/useToastContext';
import { DELETE_INSTANT_FUNDING_CARD_MUTATION } from '#client/mutations';

export default function ConfirmDeleteAstraDebitCardModal({
  id,
  handleSuccess = () => {},
  handleClose
}) {
  const { createSuccessToast, createErrorToast } = useToastContext();

  useModalDisplayTracker({
    elementName: 'delete_card_instant_deposit_screen',
    elementText: 'delete_card_instant_deposit_screen',
    appPageName: 'zb_banking_dashboard'
  });

  const [deleteInstantFundingCard, { loading: loadingDeleteInstantFundingCard }] = useMutation(
    DELETE_INSTANT_FUNDING_CARD_MUTATION,
    {
      noThrow: true
    }
  );

  const trackConfirmCancelPressed = useButtonPressedCallback({
    elementName: 'cancel_delete_instant_deposit',
    elementText: 'No, cancel',
    appPageName: 'active_banking_dashboard'
  });
  const handleCancelWithTrack = useCallback((event) => {
    event?.stopPropagation();
    trackConfirmCancelPressed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackConfirmDeletePressed = useButtonPressedCallback({
    elementName: 'confirm_delete_instant_deposit',
    elementText: 'Yes, delete',
    appPageName: 'active_banking_dashboard'
  });
  const handleDeleteDebitCard = useCallback(async () => {
    trackConfirmDeletePressed();

    const { error: deleteInstantFundingCardError } = await deleteInstantFundingCard({
      variables: {
        cardUuid: id
      },
      skip: loadingDeleteInstantFundingCard
    });

    if (deleteInstantFundingCardError) {
      // Show toast error
      createErrorToast({
        title: 'Debit card was not updated',
        description: 'There was an error updating this debit card. Please try again.',
        error: deleteInstantFundingCardError
      });
    } else {
      createSuccessToast({
        title: `Card is deleted`,
        description: 'This card is no longer linked for deposits.'
      });
      handleSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deleteInstantFundingCard,
    id,
    loadingDeleteInstantFundingCard,
    createErrorToast,
    createSuccessToast,
    handleSuccess
  ]);

  return (
    <ConfirmModal
      cancelText="No, cancel"
      handleCancel={handleCancelWithTrack}
      handleClose={handleClose}
      handleOK={handleDeleteDebitCard}
      loading={loadingDeleteInstantFundingCard}
      message="To use this card for deposits to ZenBusiness Banking, you'll need to add it again."
      okText="Yes, delete"
      title="Are you sure you want to delete this card?"
    />
  );
}

ConfirmDeleteAstraDebitCardModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  handleSuccess: PropTypes.func
};
